import { useEffect, useState, useRef } from 'react';

export interface UseQueryOptions<TData, TError = Error> {
  enabled?: boolean;
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
  retry?: number | boolean;
  retryDelay?: number;
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
  onSettled?: (data: TData | undefined, error: TError | null) => void;
}

export interface UseQueryResult<TData, TError = Error> {
  data: TData | undefined;
  error: TError | null;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  refetch: () => Promise<void>;
}

export function useQuery<TData = unknown, TError = Error>(
  queryKey: unknown[],
  queryFn: () => Promise<TData>,
  options: UseQueryOptions<TData, TError> = {}
): UseQueryResult<TData, TError> {
  const {
    enabled = true,
    refetchOnMount = true,
    refetchOnWindowFocus = false,
    retry = 0,
    retryDelay = 1000,
    onSuccess,
    onError,
    onSettled,
  } = options;

  const [data, setData] = useState<TData | undefined>(undefined);
  const [error, setError] = useState<TError | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const queryKeyRef = useRef<string>(JSON.stringify(queryKey));
  const mountedRef = useRef<boolean>(false);
  const retryCountRef = useRef<number>(0);

  const fetchData = async () => {
    if (!enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await queryFn();
      setData(result);
      setError(null);
      retryCountRef.current = 0;

      onSuccess?.(result);
      onSettled?.(result, null);
    } catch (err) {
      const queryError = err as TError;

      // Handle retry logic
      const shouldRetry = typeof retry === 'boolean' ? retry : retryCountRef.current < retry;

      if (shouldRetry) {
        retryCountRef.current += 1;
        setTimeout(() => {
          void fetchData();
        }, retryDelay);
        return;
      }

      setError(queryError);
      setData(undefined);

      onError?.(queryError);
      onSettled?.(undefined, queryError);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const currentQueryKey = JSON.stringify(queryKey);

    // Check if query key changed
    if (queryKeyRef.current !== currentQueryKey) {
      queryKeyRef.current = currentQueryKey;
      retryCountRef.current = 0;
      void fetchData();
      return;
    }

    // Initial fetch or refetch on mount
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (refetchOnMount) {
        void fetchData();
      }
    }
  }, [enabled, ...queryKey]);

  useEffect(() => {
    if (!refetchOnWindowFocus) return;

    const handleFocus = () => {
      void fetchData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetchOnWindowFocus, ...queryKey]);

  return {
    data,
    error,
    isLoading,
    isError: error !== null,
    isSuccess: data !== undefined && error === null,
    refetch: fetchData,
  };
}
